import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Check, CheckCheck, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { FONT_AR, FONT_DISPLAY, visitorId } from "../lib/sevenTheme";

/**
 * Floating WhatsApp-styled conversion chatbot for the 7 ليالي بيننا landing.
 * Bottom-right pulsing icon. On open it greets in Gulf Arabic with feminine
 * singular tone and offers 4 scripted FAQs that funnel the visitor toward
 * checkout. Every interaction (open, message, cta) is recorded via the existing
 * /api/seven/track endpoint so the admin panel can surface engagement → CTA
 * conversion rates.
 */

const PRIMARY = "#25D366"; // WhatsApp green
const PRIMARY_DARK = "#128C7E";
const HEADER_BG = "#075E54";

const send = (event, props = {}) => {
  try {
    const payload = JSON.stringify({ event, props, visitor_id: visitorId(), page: "landing-chatbot" });
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/seven/track`;
    if (navigator.sendBeacon) navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
    else api.post("/seven/track", JSON.parse(payload));
  } catch (_) { /* analytics never breaks the funnel */ }
};

// Pre-scripted Gulf-Arabic FAQ flow. Each answer ends with a CTA back to the
// main path so the conversation always points to checkout.
const FLOW = {
  start: {
    bot: "هلا فيكِ يا قلبي 🌙 أنا هنا أساعدكِ تختارين النسخة المناسبة لكِ. وش الشي اللي تحبين تعرفينه؟",
    quickReplies: ["وش يحتوي البرنامج؟", "هل المحتوى خاص ومحترم؟", "كم السعر وكيف الدفع؟", "هل في ضمان؟", "ابدئي الآن"],
  },
  "وش يحتوي البرنامج؟": {
    bot: "البرنامج فيه 7 ليالٍ مكتوبة لكِ خطوة بخطوة + بطاقات أسئلة + رسائل واتساب جاهزة + أفكار ليالٍ بيتية. كل شي مصمم بذوق خليجي راقي 🌸 تحبين تشوفين الباقات؟",
    quickReplies: ["اعرضي الباقات", "هل هو مناسب للعرسان؟", "ابدئي الآن"],
  },
  "هل المحتوى خاص ومحترم؟": {
    bot: "أكيد يا الغالية 🤍 المحتوى مكتوب بذوق وخصوصية تامة، بدون أي إحراج. كل شي يفتح من جوالكِ فقط — ولا أحد يطّلع.",
    quickReplies: ["وش يحتوي البرنامج؟", "هل في ضمان؟", "ابدئي الآن"],
  },
  "كم السعر وكيف الدفع؟": {
    bot: "عندنا 3 باقات تختارين منها:\n• نسخة البداية — 99 ريال\n• نسخة الرجوع — 149 ريال\n• النسخة الكاملة — 199 ريال ★\nالدفع آمن عبر Stripe، تقبل Apple Pay وVisa وMastercard 💳",
    quickReplies: ["اعرضي الباقات", "هل في ضمان؟", "ابدئي الآن"],
  },
  "هل في ضمان؟": {
    bot: "نعم 🤍 ضمان استرداد كامل خلال 7 أيام إذا ما عجبكِ. بدون أسئلة محرجة، تواصلين معنا ونرجّع لكِ المبلغ.",
    quickReplies: ["اعرضي الباقات", "ابدئي الآن"],
  },
  "هل هو مناسب للعرسان؟": {
    bot: "نعم 💍 عندنا نسخة كاملة للعرسان، ونسخة للأزواج بعد الروتين. النسخة الكاملة تجمع الاثنتين بسعر مميز.",
    quickReplies: ["اعرضي الباقات", "ابدئي الآن"],
  },
  "اعرضي الباقات": {
    bot: "هذه باقاتنا بنظرة سريعة:\n\n🌸 البداية — 99 ريال\n🌙 الرجوع — 149 ريال\n✨ الكاملة — 199 ريال (الأكثر طلبًا)\n\nأي واحدة تناسبكِ؟",
    quickReplies: ["ابدئي بـ 99", "ابدئي بـ 149", "ابدئي الكاملة ★"],
  },
};

const Bubble = ({ from, text, time }) => (
  <div className={`flex ${from === "bot" ? "justify-start" : "justify-end"} px-3`}>
    <div
      className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-[13.5px] leading-[1.7] shadow-sm whitespace-pre-line ${
        from === "bot" ? "bg-white text-[#111B21] rounded-tr-2xl rounded-tl-sm" : "bg-[#D9FDD3] text-[#111B21] rounded-tl-2xl rounded-tr-sm"
      }`}
      dir="rtl"
      style={{ fontFamily: FONT_AR }}
    >
      {text}
      <div className="flex items-center justify-end gap-1 mt-1 text-[10px] text-[#667781]">
        <span>{time}</span>
        {from === "user" && <CheckCheck className="w-3 h-3 text-[#53BDEB]" />}
        {from === "bot" && <Check className="w-3 h-3 text-[#667781]" />}
      </div>
    </div>
  </div>
);

export default function SevenChatbot() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [typing, setTyping] = useState(false);
  const scrollerRef = useRef(null);

  const nowTime = () => new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, typing]);

  const pushBot = (text, qr = []) => {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages((prev) => [...prev, { from: "bot", text, time: nowTime() }]);
      setQuickReplies(qr);
    }, 650);
  };

  const handleOpen = () => {
    if (open) return;
    setOpen(true);
    send("chatbot_open");
    // Initial greeting (only first time)
    if (messages.length === 0) {
      const start = FLOW.start;
      pushBot(start.bot, start.quickReplies);
    }
  };

  const handleClose = () => setOpen(false);

  const goCheckout = (bundleKey, label) => {
    send("chatbot_cta_click", { bundle: bundleKey, label });
    navigate(`/7days/checkout?bundle=${bundleKey}`);
  };

  const handleReply = (label) => {
    setQuickReplies([]);
    setMessages((prev) => [...prev, { from: "user", text: label, time: nowTime() }]);
    send("chatbot_message", { reply: label });

    // CTA short-circuits
    if (label === "ابدئي الآن" || label === "ابدئي الكاملة ★") return goCheckout("complete", label);
    if (label === "ابدئي بـ 99") return goCheckout("starter", label);
    if (label === "ابدئي بـ 149") return goCheckout("reset", label);

    const node = FLOW[label];
    if (node) pushBot(node.bot, node.quickReplies);
    else pushBot("ودي أساعدكِ أكثر 🤍 اختاري من الخيارات تحت.", FLOW.start.quickReplies);
  };

  return (
    <>
      {/* Floating button — premium dark + gold, subtle. Sized down from a
          flashy WhatsApp bubble to a refined pill that matches the site's
          luxury aesthetic. A tiny green dot signals it's a live chat. */}
      {!open && (
        <button
          onClick={handleOpen}
          aria-label="افتحي المحادثة"
          data-testid="seven-chatbot-fab"
          className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6 z-[60] group"
        >
          <div
            className="relative flex items-center justify-center w-[46px] h-[46px] rounded-full transition-transform group-hover:scale-[1.05] group-active:scale-95"
            style={{
              background: "linear-gradient(180deg,#1C1010 0%,#0F0808 100%)",
              border: "1px solid rgba(201,166,106,0.45)",
              boxShadow: "0 10px 26px -10px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.04)",
            }}
          >
            <MessageCircle className="w-[18px] h-[18px] text-[#C9A66A]" strokeWidth={1.8} />
            {/* Tiny presence dot — premium WhatsApp recognition without the noise */}
            <span
              className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full"
              style={{ background: PRIMARY, boxShadow: "0 0 0 1.5px #0A0606" }}
            />
          </div>
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div
          dir="rtl"
          lang="ar"
          data-testid="seven-chatbot-window"
          className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6 z-[60] w-[calc(100vw-32px)] sm:w-[360px] max-w-[380px] h-[70vh] sm:h-[540px] rounded-2xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)] flex flex-col border border-black/10"
          style={{ background: "#ECE5DD", fontFamily: FONT_AR }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-3.5 py-2.5" style={{ background: HEADER_BG }}>
            <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center border border-white/20">
              <img src="/images/7nights-logo.png" alt="7 ليالي بيننا" className="w-7 h-7 object-contain" />
            </div>
            <div className="flex-1 leading-tight">
              <div className="text-white text-[14px] font-bold" style={{ fontFamily: FONT_DISPLAY }}>7 ليالي بيننا</div>
              <div className="text-[#B9D9D2] text-[11px] flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#4ADE80] inline-block" />
                متصلة الآن
              </div>
            </div>
            <button
              onClick={handleClose}
              aria-label="إغلاق"
              data-testid="seven-chatbot-close"
              className="text-white/80 hover:text-white p-1.5"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Conversation area */}
          <div
            ref={scrollerRef}
            className="flex-1 overflow-y-auto py-3 space-y-2"
            style={{ backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"40\" height=\"40\"><circle cx=\"20\" cy=\"20\" r=\"0.6\" fill=\"%23000\" opacity=\"0.04\"/></svg>')" }}
          >
            {messages.map((m, i) => (
              <Bubble key={i} from={m.from} text={m.text} time={m.time} />
            ))}
            {typing && (
              <div className="flex justify-start px-3">
                <div className="bg-white rounded-2xl rounded-tr-2xl rounded-tl-sm px-3.5 py-2.5 shadow-sm">
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#667781] animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#667781] animate-bounce" style={{ animationDelay: "120ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#667781] animate-bounce" style={{ animationDelay: "240ms" }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Quick replies */}
          {!typing && quickReplies.length > 0 && (
            <div className="px-3 py-2.5 bg-[#ECE5DD] border-t border-black/[0.06] space-y-1.5" data-testid="seven-chatbot-quickreplies">
              {quickReplies.map((qr, i) => {
                const isCta = qr.startsWith("ابدئي");
                return (
                  <button
                    key={i}
                    onClick={() => handleReply(qr)}
                    data-testid={`seven-chatbot-qr-${i}`}
                    className={`w-full text-right text-[13px] font-bold px-3.5 py-2.5 rounded-full transition-colors border ${
                      isCta
                        ? "bg-gradient-to-b from-[#25D366] to-[#128C7E] text-white border-transparent hover:opacity-95 shadow-[0_6px_20px_-6px_rgba(37,211,102,0.55)] flex items-center justify-center gap-2"
                        : "bg-white text-[#075E54] border-[#075E54]/15 hover:bg-[#075E54]/[0.04]"
                    }`}
                  >
                    {qr}
                    {isCta && <ArrowLeft className="w-3.5 h-3.5" />}
                  </button>
                );
              })}
            </div>
          )}

          {/* Faux input bar — purely decorative, signals the WhatsApp feel */}
          <div className="px-2 py-2 bg-[#F0F2F5] flex items-center gap-2">
            <div className="flex-1 bg-white rounded-full px-4 py-2 text-[12.5px] text-[#8696A0]">اختاري من الخيارات أعلاه…</div>
            <button
              type="button"
              aria-label="إرسال"
              className="w-9 h-9 rounded-full flex items-center justify-center text-white"
              style={{ background: PRIMARY_DARK }}
              onClick={() => goCheckout("complete", "send-button")}
              data-testid="seven-chatbot-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
